import { Injectable, inject } from '@angular/core';
import { MarketingOrderNotesClient } from '../clients/marketing-order-notes.client';
import { NoteForm } from '../forms/note.form';
import { Note, NoteCategory } from '../models/note.model';

/**
 * The NotesService is a layer between the components and the API Clients.
 * The purpose of the service is to handle business logic while interacting with the
 * clients. This eliminates the components from needing to handle business logic.
 * All business logic should be done here
 */
@Injectable({
  providedIn: 'root',
})
export class MarketingOrderNotesService {
  private readonly noteClient = inject(MarketingOrderNotesClient);

  /**
   * Loads the notes belonging to a specific marketing order.
   * @param orderId - The order ID
   * @param category - Optional category filter
   */
  getNotesFor(orderId: string, category?: NoteCategory): Promise<Note[]> {
    // Note: If we want to do caching or utilize the ngrx store, do it here
    return this.noteClient.get(orderId, category);
  }

  /**
   * Loads the agent notes for a specific user/agent.
   * @param userId - The user/agent ID
   * @param category - Optional category filter
   */
  getAgentNotes(userId: string, category?: NoteCategory): Promise<Note[]> {
    return this.noteClient.getAgentNotes(userId, category);
  }

  /**
   * Submits a notification to the API
   * @param orderId - The order ID
   * @param form - The note form
   */
  create(orderId: string, form: NoteForm): Promise<Note[]> {
    return this.noteClient.post(orderId, form.value);
  }

  /**
   * Submits an agent note to the API
   * @param userId - The user/agent ID
   * @param form - The note form
   */
  createAgentNote(userId: string, form: NoteForm): Promise<Note[]> {
    return this.noteClient.postAgentNote(userId, form.value);
  }

  /**
   * Updates an existing note
   * @param orderId - The order ID
   * @param noteId - The note ID to update
   * @param form - The note form with updated values
   */
  update(orderId: string, noteId: string, form: NoteForm): Promise<Note[]> {
    return this.noteClient.put(orderId, noteId, form.value);
  }

  /**
   * Updates an existing agent note
   * @param userId - The user/agent ID
   * @param noteId - The note ID to update
   * @param form - The note form with updated values
   */
  updateAgentNote(userId: string, noteId: string, form: NoteForm): Promise<Note[]> {
    return this.noteClient.putAgentNote(userId, noteId, form.value);
  }

  /**
   * Deletes a note
   * @param orderId - The order ID
   * @param noteId - The note ID to delete
   */
  delete(orderId: string, noteId: string): Promise<Note[]> {
    return this.noteClient.delete(orderId, noteId);
  }

  /**
   * Deletes an agent note
   * @param userId - The user/agent ID
   * @param noteId - The note ID to delete
   */
  deleteAgentNote(userId: string, noteId: string): Promise<Note[]> {
    return this.noteClient.deleteAgentNote(userId, noteId);
  }
}
